import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Table,
  UncontrolledDropdown,
} from 'reactstrap';
import { WorkPhase } from '../models/investors';
import {
  PROJECT_WORK_PHASE_ENUM,
  PROJECT_WORK_PHASE_LABELS,
  PROJECT_WORK_PHASE_TO_PROGRESS,
  PROJECT_WORK_PROGRESS,
} from '../constants/projectConstants';

interface Props {
  phases: WorkPhase[];
  loaded: boolean;
  apiErrors: string | null;
  projectId: string;
  onUpdateProgress: (phaseId: string, progress: number) => Promise<void>;
}

const PROGRESS_BADGE_COLOR: Record<string, string> = {
  in_plans: 'info',
  in_construction: 'warning',
  finalized: 'success',
};

const formatDate = (value: string | null) => {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('es-SV', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
};

const WorkPhasesTable = ({ phases, loaded, apiErrors, projectId, onUpdateProgress }: Props) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPhase, setSelectedPhase] = useState<WorkPhase | null>(null);
  const [progressValue, setProgressValue] = useState<number>(0);
  const [saving, setSaving] = useState(false);

  const openProgressModal = (phase: WorkPhase) => {
    setSelectedPhase(phase);
    setProgressValue(phase.phase_progress ?? 0);
    setModalOpen(true);
  };

  const handleSaveProgress = async () => {
    if (!selectedPhase) return;
    setSaving(true);
    await onUpdateProgress(selectedPhase.id, progressValue);
    setSaving(false);
    setModalOpen(false);
  };

  if (!loaded) return <p>Cargando...</p>;
  if (apiErrors) return <p className="text-danger">{apiErrors}</p>;
  if (phases.length === 0)
    return (
      <p className="text-muted">No hay fases registradas para este proyecto.</p>
    );

  const groupedByProgress = PROJECT_WORK_PROGRESS.map((progress) => ({
    ...progress,
    phases: phases.filter(
      (p) => PROJECT_WORK_PHASE_TO_PROGRESS[p.phase] === progress.value,
    ),
  }));

  return (
    <>
      <div style={{ height: '500px', overflow: 'auto' }}>
        <Table className="align-items-center" responsive>
          <thead className="thead-light">
            <tr>
              <th scope="col">Sub-fase</th>
              <th scope="col">Inicio</th>
              <th scope="col">Fin</th>
              <th scope="col" />
            </tr>
          </thead>
          <tbody>
            {groupedByProgress
              .filter((g) => g.phases.length > 0)
              .flatMap((group) => [
                <tr key={`group-${group.value}`}>
                  <td
                    colSpan={4}
                    className={`bg-${PROGRESS_BADGE_COLOR[group.value]} text-center text-white font-weight-bold`}
                    style={{ paddingTop: '0.5rem', paddingBottom: '0.5rem' }}
                  >
                    {group.display_value}
                  </td>
                </tr>,
                ...group.phases.map((phase) => (
                  <tr key={phase.id}>
                    <td>{PROJECT_WORK_PHASE_LABELS[phase.phase] ?? phase.phase}</td>
                    <td>{formatDate(phase.started_at)}</td>
                    <td>{formatDate(phase.ended_at)}</td>
                    <td className="text-right">
                      {phase.phase === PROJECT_WORK_PHASE_ENUM.CONSTRUCTION_START && (
                        <UncontrolledDropdown>
                          <DropdownToggle color="secondary" size="sm" caret />
                          <DropdownMenu right>
                            <DropdownItem onClick={() => openProgressModal(phase)}>
                              <i className="fa-solid fa-chart-line mr-2" />
                              Actualizar progreso
                            </DropdownItem>
                            <DropdownItem
                              tag={Link}
                              to={`/investors/projects/${projectId}/work-phases/${phase.id}/reports`}
                            >
                              <i className="fa-solid fa-file-lines mr-2" />
                              Gestión de Reportes
                            </DropdownItem>
                          </DropdownMenu>
                        </UncontrolledDropdown>
                      )}
                    </td>
                  </tr>
                )),
              ])}
          </tbody>
        </Table>
      </div>

      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)}>
        <ModalHeader toggle={() => setModalOpen(false)}>
          Actualizar progreso de construcción
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label for="phase-progress">Progreso (%)</Label>
            <Input
              id="phase-progress"
              type="number"
              min={0}
              max={100}
              value={progressValue}
              onChange={(e) => setProgressValue(Number(e.target.value))}
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="success" onClick={handleSaveProgress} disabled={saving}>
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
          <Button color="secondary" onClick={() => setModalOpen(false)}>
            Cancelar
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default WorkPhasesTable;
